import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ContractEditorToolbar } from "@/business/contract/ContractEditorToolbar";
import {
  ContractRichEditor,
  plainToJson,
  jsonToPlainMarker,
  type RichEditorHandle,
} from "@/business/contract/ContractRichEditor";
import { ContractPinGate } from "@/business/contract/ContractPinGate";
import { ContractFieldToolbar } from "@/business/contract/ContractFieldOverlay";
import {
  ContractField,
  ContractFieldOptions,
  ContractTemplateData,
  EMPTY_TEMPLATE,
  FieldType,
} from "@/business/contract/contractTemplate";
import { useContractTemplateStore } from "&/contractTemplate";
import {
  useContractTemplateDetail,
  useCreateContractTemplate,
  useUpdateContractTemplate,
} from "~/services/business/contractTemplate";
import { useSealList } from "~/services/business/seal";
import { useLoadingStore } from "&/loading";
import useHideNav from "^/common/useHideNav";
import type {
  CdContractEditStep,
  CdContractStatus,
  CdFieldType,
  ContractFieldDto,
  ContractTemplateDetailResponse,
} from "~/types/common";

export const Route = createFileRoute("/business/layout/contract-template-edit")(
  {
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>): { seq?: string } => ({
      seq: search.seq ? String(search.seq) : undefined,
    }),
  },
);

/* ─── FE FieldType ↔ BE CdFieldType 매핑 ─── */
const FE_TO_BE_FIELD: Record<FieldType, CdFieldType> = {
  variable: "FIELD_TYPE_VAR",
  text: "FIELD_TYPE_TEXT",
  longText: "FIELD_TYPE_TEXT",
  date: "FIELD_TYPE_DATE",
  checkbox: "FIELD_TYPE_CHECKBOX",
  signature: "FIELD_TYPE_SIGN",
  seal: "FIELD_TYPE_SEAL",
  address: "FIELD_TYPE_ADDRESS",
};
const BE_TO_FE_FIELD: Record<CdFieldType, FieldType> = {
  FIELD_TYPE_VAR: "variable",
  FIELD_TYPE_TEXT: "text",
  FIELD_TYPE_DATE: "date",
  FIELD_TYPE_CHECKBOX: "checkbox",
  FIELD_TYPE_SIGN: "signature",
  FIELD_TYPE_SEAL: "seal",
  FIELD_TYPE_ADDRESS: "address",
};

type EditStep = "body" | "fields" | "preview";

const NEXT_EDIT_STEP_AFTER_SAVE: Record<EditStep, CdContractEditStep> = {
  body: "CONTRACT_EDIT_FIELD",
  fields: "CONTRACT_EDIT_REVIEW",
  preview: "CONTRACT_EDIT_REVIEW",
};

const parseFieldOptions = (
  raw?: string | null,
): ContractFieldOptions | undefined => {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const stringifyFieldOptions = (
  options?: ContractFieldOptions,
): string | undefined => {
  if (!options || Object.keys(options).length === 0) return undefined;
  return JSON.stringify(options);
};

const dtoToTemplateField = (
  f: ContractFieldDto,
  idx: number,
): ContractField => {
  const parsedOptions = parseFieldOptions(f.fieldOptionsJson);
  const type =
    f.cdFieldType === "FIELD_TYPE_TEXT" &&
    parsedOptions?.textMode === "MULTILINE"
      ? "longText"
      : BE_TO_FE_FIELD[f.cdFieldType];
  return {
    id: f.fieldSeq != null ? `f-${f.fieldSeq}` : `f-${Date.now()}-${idx}`,
    type,
    key: type === "seal" ? "companySeal" : f.fieldKey,
    label: f.fieldLabel,
    auto: !!f.autoFill,
    required: !!f.required,
    description: f.description,
    defaultValue: f.defaultValue,
    fieldOptions:
      type === "checkbox"
        ? (parsedOptions ?? {
            checkboxDisplay: "MARK_ONLY",
            checkboxMode: "SINGLE",
          })
        : type === "longText"
          ? { ...parsedOptions, textMode: "MULTILINE" }
          : parsedOptions,
    pageNo: f.pageNo,
    x: f.posX,
    y: f.posY,
    width: f.posWidth,
    height: f.posHeight,
  };
};

const stripLegacyFieldsFromDoc = (
  input: any,
): { json: any; fields: ContractField[] } => {
  const fields: ContractField[] = [];

  const walk = (node: any, pageNo: number): any | null => {
    if (!node || typeof node !== "object") return node;
    // 단일레이어: 필드는 본문 inline 노드로 유지(변수와 동일). 여기서 벗겨내면 편집기가 못 그린다.
    //  좌표/옵션은 서명/렌더 시 Playwright manifest 실측으로 결정되므로 별도 fields 로 빼지 않는다.
    if (node.type === "contractField") {
      return node;
    }

    const nextPageNo =
      node.type === "contractPage"
        ? Number(node.attrs?.pageNumber ?? pageNo) || pageNo
        : pageNo;
    if (!Array.isArray(node.content)) return node;

    const nextContent = node.content
      .map((child: any) => walk(child, nextPageNo))
      .filter(Boolean);

    if (node.type === "contractPage" && nextContent.length === 0) {
      return { ...node, content: [{ type: "paragraph" }] };
    }
    return { ...node, content: nextContent };
  };

  return {
    json: walk(input, 1) ?? input,
    fields,
  };
};

const mergeFields = (
  base: ContractField[],
  extra: ContractField[],
): ContractField[] => {
  const byId = new Map<string, ContractField>();
  [...base, ...extra].forEach((field) => {
    const id = `${field.type}:${field.key}`;
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, field);
      return;
    }
    // 같은 필드가 둘 이상 — 배치 좌표가 있는 쪽을 우선한다.
    // (미배치 필드가 사용자가 방금 배치한 필드를 덮어써 위치가 사라지던 문제 방지)
    if (hasPlacedTemplateField(field) && !hasPlacedTemplateField(existing)) {
      byId.set(id, field);
    }
  });
  return [...byId.values()];
};

const hasPlacedTemplateField = (field: ContractField) =>
  field.pageNo != null &&
  field.x != null &&
  field.y != null &&
  field.width != null &&
  field.height != null;

const clearFieldPlacement = (field: ContractField): ContractField => ({
  ...field,
  pageNo: undefined,
  x: undefined,
  y: undefined,
  width: undefined,
  height: undefined,
});

const isWithinRenderedPageCount = (field: ContractField, pageCount: number) =>
  !hasPlacedTemplateField(field) ||
  (pageCount > 0 && field.pageNo! >= 1 && field.pageNo! <= pageCount);

const normalizeFieldsForRenderedPages = (
  fields: ContractField[],
  pageCount: number,
) =>
  pageCount > 0
    ? fields.map((field) =>
        isWithinRenderedPageCount(field, pageCount)
          ? field
          : clearFieldPlacement(field),
      )
    : fields;

const parseDocJson = (d: ContractTemplateDetailResponse): any => {
  try {
    return d.contractDocJson
      ? JSON.parse(d.contractDocJson)
      : plainToJson(d.contractContents || "");
  } catch {
    return plainToJson(d.contractContents || "");
  }
};

const fromDetailToTemplate = (
  d: ContractTemplateDetailResponse,
): ContractTemplateData => {
  const stripped = stripLegacyFieldsFromDoc(parseDocJson(d));
  const renderedPageCount = d.renderedPageFileUrls?.length ?? 0;
  const mergedFields = mergeFields(
    (d.fields ?? []).map(dtoToTemplateField),
    stripped.fields,
  );
  return {
    contractSeq: d.contractSeq,
    name: d.contractName,
    body: d.contractContents,
    active: d.cdContractStatus === "CONTRACT_STATUS_ACTIVE",
    type: d.cdContractType ?? "CONTRACT_TYPE_CONSIGNMENT",
    autoFirstReservYn: d.autoFirstReservYn === "Y" ? "Y" : "N",
    autoVariables: d.autoVarKeys
      ? d.autoVarKeys.split(",").filter(Boolean)
      : undefined,
    fields: normalizeFieldsForRenderedPages(mergedFields, renderedPageCount),
  };
};

const toBackendFields = (fields: ContractField[]): ContractFieldDto[] =>
  fields.map((f, idx) => ({
    fieldSeq:
      f.id?.startsWith("f-") && /^f-\d+$/.test(f.id)
        ? Number(f.id.replace("f-", ""))
        : undefined,
    cdFieldType: FE_TO_BE_FIELD[f.type],
    fieldKey: f.type === "seal" ? "companySeal" : f.key,
    fieldLabel: f.label || f.key,
    fieldOrder: idx,
    autoFill: !!f.auto,
    required: !!f.required,
    defaultValue: f.defaultValue,
    description: f.description,
    fieldOptionsJson: stringifyFieldOptions(
      f.type === "longText"
        ? { ...f.fieldOptions, textMode: "MULTILINE" }
        : f.fieldOptions,
    ),
    pageNo: f.pageNo,
    posX: f.x,
    posY: f.y,
    posWidth: f.width,
    posHeight: f.height,
  }));

type EditorSeed = {
  key: string;
  json: any;
  text: string;
};

/** doc 안에 inline contractField 노드가 하나라도 있는지 */
const docHasInlineField = (node: any): boolean => {
  if (!node || typeof node !== "object") return false;
  if (node.type === "contractField") return true;
  if (Array.isArray(node.content)) return node.content.some(docHasInlineField);
  return false;
};

/**
 * 레거시 마이그레이션 — docJson 에 inline 필드가 없고 DB 필드(INFO_CONTRACT_FIELD)만 있는
 * 구형 템플릿을 단일레이어로 흡수. 오버레이를 폐기했으므로 이 필드들을 docJson inline 노드로
 * 넣지 않으면 화면에서 사라진다. 원래 절대좌표는 흐름 위치로 정확 복원 불가하므로 해당 페이지
 * 하단에 y 순서로 붙인다(데이터 보존 우선 — 사장님이 위치 재조정 가능).
 */
const injectLegacyDbFieldsIntoDoc = (
  json: any,
  fields?: ContractFieldDto[] | null,
): any => {
  if (!json || !Array.isArray(json.content) || !fields?.length) return json;
  if (docHasInlineField(json)) return json; // 이미 단일레이어
  const pages = json.content.filter((n: any) => n?.type === "contractPage");
  if (!pages.length) return json;
  const byPage = new Map<number, ContractFieldDto[]>();
  fields.forEach((f) => {
    const p = Math.min(Math.max(1, f.pageNo ?? 1), pages.length);
    if (!byPage.has(p)) byPage.set(p, []);
    byPage.get(p)!.push(f);
  });
  byPage.forEach((list, pageNo) => {
    list.sort((a, b) => (a.posY ?? 0) - (b.posY ?? 0));
    const page = pages[pageNo - 1];
    const para: any = { type: "paragraph", content: [] };
    list.forEach((f, i) => {
      if (i > 0) para.content.push({ type: "text", text: " " });
      para.content.push({
        type: "contractField",
        attrs: {
          fieldType: BE_TO_FE_FIELD[f.cdFieldType] ?? "text",
          fieldKey: f.fieldKey,
          label: f.fieldLabel,
          alignment: "left",
          required: !!f.required,
          fieldOptions: parseFieldOptions(f.fieldOptionsJson) ?? null,
        },
      });
    });
    page.content = [...(page.content || []), para];
  });
  return json;
};

const detailToEditorSeed = (d: ContractTemplateDetailResponse): EditorSeed => {
  const { json } = stripLegacyFieldsFromDoc(parseDocJson(d));
  return {
    key: `contract-${d.contractSeq}`,
    json: injectLegacyDbFieldsIntoDoc(json, d.fields),
    text: d.contractContents || "",
  };
};

const draftToEditorSeed = (draft: any | null | undefined): EditorSeed => {
  const json = draft?.docJson ?? plainToJson(draft?.body || "");
  return {
    key: draft?.docJson ? "new-imported-doc" : "new-empty-doc",
    json,
    text: draft?.body || "",
  };
};

const resumeStepFromDetail = (
  detail?: ContractTemplateDetailResponse | null,
): EditStep => {
  // 활성화된(사용 중) 템플릿은 수정 불가 — 검토(보기)만 허용하므로 무조건 검토로.
  if (detail?.cdContractStatus === "CONTRACT_STATUS_ACTIVE") {
    return "preview";
  }
  // BE 가 저장한 cdEditStep 을 신뢰.
  // Step 3 검토는 ContractRichEditor 기반이라 Playwright 렌더 이미지가 없어도 표시 가능
  // (AI 임포트 직후 REVIEW 상태 계약서도 바로 검토 화면으로).
  if (detail?.cdEditStep === "CONTRACT_EDIT_REVIEW") {
    return "preview";
  }
  if (detail?.cdEditStep === "CONTRACT_EDIT_FIELD") {
    return "fields";
  }
  return "body";
};

type DirtySnapshot = {
  name: string;
  autoFirstReservYn: "Y" | "N";
  autoVariables: string;
  bodyPlain: string;
  fields: string;
};

const serializeFieldsForDirty = (fields: ContractField[]) =>
  JSON.stringify(
    fields
      .map((f) => ({
        type: f.type,
        key: f.key,
        label: f.label,
        auto: !!f.auto,
        required: !!f.required,
        description: f.description ?? "",
        defaultValue: f.defaultValue ?? "",
        pageNo: f.pageNo ?? null,
        x: f.x ?? null,
        y: f.y ?? null,
        width: f.width ?? null,
        height: f.height ?? null,
      }))
      .sort((a, b) => `${a.type}:${a.key}`.localeCompare(`${b.type}:${b.key}`)),
  );

const snapshotOf = (
  template: ContractTemplateData,
  bodyPlain: string,
): DirtySnapshot => ({
  name: template.name,
  autoFirstReservYn: template.autoFirstReservYn,
  autoVariables: (template.autoVariables ?? []).join(","),
  bodyPlain,
  fields: serializeFieldsForDirty(template.fields),
});

const sameSnapshot = (a?: DirtySnapshot | null, b?: DirtySnapshot | null) =>
  !!a &&
  !!b &&
  a.name === b.name &&
  a.autoFirstReservYn === b.autoFirstReservYn &&
  a.autoVariables === b.autoVariables &&
  a.bodyPlain === b.bodyPlain &&
  a.fields === b.fields;

const hasPlacedDtoField = (field: ContractFieldDto) =>
  field.pageNo != null &&
  field.posX != null &&
  field.posY != null &&
  field.posWidth != null &&
  field.posHeight != null;

function RouteComponent() {
  useHideNav(); // 편집기는 한 화면 고정 몰입 UI — 하단 nav 숨김
  const { seq } = Route.useSearch();
  const { history } = useRouter();
  const isNew = !seq || seq === "new";
  const seqNum = !isNew ? Number(seq) : undefined;

  const { importedDraft, clearImportedDraft } = useContractTemplateStore();
  const { toggleLoading } = useLoadingStore();
  const importedDraftRef = useRef(importedDraft);
  const { data: detail, isFetching: isDetailFetching } =
    useContractTemplateDetail(seqNum);
  useSealList(); // 도장 목록 프리페치 (미리보기 캐시용)
  const createMutation = useCreateContractTemplate();
  const updateMutation = useUpdateContractTemplate();
  const savePending = createMutation.isPending || updateMutation.isPending;

  // 템플릿 상태
  const [template, setTemplate] = useState<ContractTemplateData>(
    isNew
      ? importedDraft
        ? {
            ...EMPTY_TEMPLATE,
            name: importedDraft.suggestedName,
            body: importedDraft.body,
            fields: importedDraft.fields,
            active: false,
          }
        : EMPTY_TEMPLATE
      : EMPTY_TEMPLATE,
  );
  const [step, setStep] = useState<EditStep>("body");
  const [isDirty, setIsDirty] = useState(false);
  const savedSnapshotRef = useRef<DirtySnapshot | null>(null);
  const ignoreNextEditorChangeRef = useRef(true);
  const shouldResumeStepRef = useRef(true);
  const pendingStepAfterSaveRef = useRef<EditStep | null>(null);
  const loadedContractSeqRef = useRef<number | null>(null);
  const editorRef = useRef<RichEditorHandle | null>(null);
  const [editorSeed, setEditorSeed] = useState<EditorSeed | null>(
    isNew ? draftToEditorSeed(importedDraft) : null,
  );
  const latestFieldsRef = useRef<ContractField[]>(template.fields);
  const seedVersionRef = useRef(0);
  const latestEditorJsonRef = useRef<any>(editorSeed?.json ?? null);
  const latestEditorPlainRef = useRef<string>(
    editorSeed ? jsonToPlainMarker(editorSeed.json) : "",
  );

  const isSnapshotDirty = (
    nextTemplate: ContractTemplateData,
    nextPlain = latestEditorPlainRef.current,
  ) =>
    !sameSnapshot(
      savedSnapshotRef.current,
      snapshotOf(nextTemplate, nextPlain),
    );

  const updateTemplateState = (
    updater: (prev: ContractTemplateData) => ContractTemplateData,
  ) => {
    setTemplate((prev) => {
      const next = updater(prev);
      latestFieldsRef.current = next.fields;
      setIsDirty(isSnapshotDirty(next));
      return next;
    });
  };

  useEffect(() => {
    if (isNew && !savedSnapshotRef.current) {
      savedSnapshotRef.current = snapshotOf(
        template,
        latestEditorPlainRef.current,
      );
    }
    // 초기 기준점만 잡는다. 이후 변경 감지는 updateTemplateState / editor onChange 에서 처리한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    toggleLoading(savePending);
    return () => {
      if (savePending) toggleLoading(false);
    };
  }, [savePending, toggleLoading]);

  // 재진입 감지 — 라우트 언마운트 → 리마운트로 loadedContractSeqRef 가 초기화되므로,
  // 이 컴포넌트가 처음 마운트되는 순간에는 무조건 BE cdEditStep 을 신뢰해서 tab 복귀.
  const didInitialResumeRef = useRef(false);

  // 서버에서 받은 detail 로 템플릿 채움
  useEffect(() => {
    if (!isNew && detail) {
      const changedContract =
        loadedContractSeqRef.current !== detail.contractSeq;
      if (changedContract) {
        loadedContractSeqRef.current = detail.contractSeq;
        shouldResumeStepRef.current = true;
      }
      ignoreNextEditorChangeRef.current = true;
      const nextTemplate = fromDetailToTemplate(detail);
      const nextSeed = detailToEditorSeed(detail);
      latestFieldsRef.current = nextTemplate.fields;
      latestEditorJsonRef.current = nextSeed.json;
      latestEditorPlainRef.current = jsonToPlainMarker(nextSeed.json);
      savedSnapshotRef.current = snapshotOf(
        nextTemplate,
        latestEditorPlainRef.current,
      );
      setTemplate(nextTemplate);
      setEditorSeed((prev) => {
        if (prev?.key === nextSeed.key) return prev;
        return nextSeed;
      });
      setIsDirty(false);
      if (shouldResumeStepRef.current) {
        setStep(resumeStepFromDetail(detail));
        shouldResumeStepRef.current = false;
      }
    }
  }, [isNew, detail]);

  // ── 재진입 방어망 ──
  // 라우트 리마운트 직후 첫 번째 detail 이 도달했는데도 step 이 "body" 인 상태로 남는
  // 케이스(예: React Query 캐시 hit → useEffect 순서 어긋남) 를 잡기 위한 안전장치.
  // BE 가 저장한 cdEditStep 이 FIELD/REVIEW 인데 로컬 step 이 body 라면 강제 정렬.
  useEffect(() => {
    if (isNew || !detail || isDetailFetching) return;
    if (didInitialResumeRef.current) return;
    didInitialResumeRef.current = true;
    const target = resumeStepFromDetail(detail);
    if (target !== step) setStep(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, detail?.contractSeq, detail?.cdEditStep, isDetailFetching]);

  const hasRenderedPages = !!(
    detail?.latestRenderStatus === "CONTRACT_RENDER_STATUS_READY" &&
    detail?.renderedPageFileUrls?.length
  );
  const hasRenderedPreview = !!(hasRenderedPages && detail?.placementManifest);
  const isActiveTemplate =
    detail?.cdContractStatus === "CONTRACT_STATUS_ACTIVE";
  const activeTemplateLockMessage =
    "활성화된 계약서는 수정할 수 없어요. 목록에서 비활성화 후 수정해 주세요.";
  // 1순위: BE 가 저장한 detail.cdEditStep 을 신뢰.
  //   (이전에는 hasRenderedPages 만 보고 렌더 미완이면 무조건 BODY 로 강제해
  //    본문 저장 후 자동 전진이 전부 막히는 문제가 있었다.)
  // 2순위: 없거나 활성/렌더 상태 기준으로 재도출.
  const persistedEditStep = (() => {
    if (isActiveTemplate) return "CONTRACT_EDIT_REVIEW";
    const beStep = detail?.cdEditStep;
    if (
      beStep === "CONTRACT_EDIT_REVIEW" ||
      beStep === "CONTRACT_EDIT_FIELD" ||
      beStep === "CONTRACT_EDIT_BODY"
    ) {
      return beStep;
    }
    if (!hasRenderedPages) return "CONTRACT_EDIT_BODY";
    const hasPlacedFields = !!detail?.fields?.some(hasPlacedDtoField);
    if (hasPlacedFields && hasRenderedPreview) {
      return "CONTRACT_EDIT_REVIEW";
    }
    return "CONTRACT_EDIT_FIELD";
  })();
  const effectiveDirty = !isActiveTemplate && isDirty;
  // 필드 배치 화면용 페이지 URL 배열.
  //   1) 실제 렌더 이미지가 있으면 그대로 사용
  //   2) 없으면 본문(docJson) 의 contractPage 개수만큼 빈 URL 로 채워
  //      빈 A4 캔버스에 필드를 배치할 수 있게 한다 (Playwright 렌더 없이도 UI 사용 가능).
  const effectivePageUrls = useMemo<string[]>(() => {
    if (detail?.renderedPageFileUrls?.length)
      return detail.renderedPageFileUrls;
    // docJson 파싱 실패해도 최소 1 페이지 (빈 상태)
    try {
      const doc = detail?.contractDocJson
        ? typeof detail.contractDocJson === "string"
          ? JSON.parse(detail.contractDocJson)
          : detail.contractDocJson
        : (latestEditorJsonRef.current ?? editorSeed?.json);
      const pageCount = Array.isArray(doc?.content)
        ? doc.content.filter((n: any) => n?.type === "contractPage").length
        : 0;
      return Array(Math.max(1, pageCount)).fill("");
    } catch {
      return [""];
    }
  }, [detail?.renderedPageFileUrls, detail?.contractDocJson, editorSeed]);

  useEffect(() => {
    if (isActiveTemplate && isDirty) {
      ignoreNextEditorChangeRef.current = true;
      setIsDirty(false);
    }
  }, [isActiveTemplate, isDirty]);
  // 탭 활성 정책 (단순화):
  //   - Step 1 본문: 항상 열림
  //   - Step 2 필드: 한 번이라도 저장된 계약(seqNum 존재) 이면 열림
  //   - Step 3 검토: BE 가 저장한 cdEditStep 이 REVIEW (=필드 저장 완료) 이면 열림
  //   ─ 활성화된(사용 중) 템플릿은 편집 잠금
  const savedOnce = !isNew && seqNum != null;
  // 활성화된 템플릿: 본문·필드는 잠그고 검토(보기)만 허용.
  const bodyDisabled = isActiveTemplate;
  const bodyDisabledReason = isActiveTemplate ? activeTemplateLockMessage : "";
  const fieldsDisabledReason = isNew
    ? "본문을 먼저 저장하면 필드 설정을 할 수 있어요"
    : isActiveTemplate
      ? activeTemplateLockMessage
      : "";
  const fieldsDisabled = !savedOnce || isActiveTemplate;
  const previewDisabledReason = isNew
    ? "본문을 먼저 저장하면 검토할 수 있어요"
    : isActiveTemplate
      ? "" // 활성 템플릿은 검토(보기) 허용 — 잠금 아님
      : persistedEditStep !== "CONTRACT_EDIT_REVIEW"
        ? "필드 설정을 저장하면 검토할 수 있어요"
        : "";
  // 활성 템플릿은 검토를 항상 열어준다(보기용). 그 외에는 저장·단계 조건을 따른다.
  const finalPreviewDisabled = isActiveTemplate
    ? false
    : !savedOnce || persistedEditStep !== "CONTRACT_EDIT_REVIEW";

  useEffect(() => {
    if (isDetailFetching || !detail) return;

    // 활성화된 템플릿은 수정 불가 — 본문/필드로 들어가려 하면 검토(보기)로 고정.
    if (isActiveTemplate) {
      if (step !== "preview") setStep("preview");
      return;
    }
    // 필드 스텝 가드 — 신규 만들기 중이면 body 로 복귀.
    // (BE 가 저장한 cdEditStep 이 BODY 라도, 사용자가 방금 fields 로 전진했으면
    //  detail 캐시 갱신 지연 사이 revert 되는 문제 방지 → beStep === BODY 조건 제거)
    if (step === "fields" && isNew) {
      setStep("body");
    }
    // 검토 스텝 가드 — 신규 작성 중일 때만 되돌림.
    // 이전에는 `beStep !== REVIEW`(refetch 캐시 지연 중 이전 값) 와
    // `!hasRenderedPreview`(Playwright 렌더 필수) 조건 때문에
    // 필드 저장 → 검토 진입 직후 본문으로 튕기는 문제가 있었다.
    // 검토 화면은 편집기 기반이라 렌더 이미지 불필요하고,
    // 검토 탭 진입 자체가 finalPreviewDisabled 로 이미 가드되므로 여기선 isNew 만 확인.
    if (step === "preview" && isNew) {
      setStep("body");
    }
  }, [detail, isDetailFetching, isActiveTemplate, isNew, step]);

  useEffect(() => {
    const targetStep = pendingStepAfterSaveRef.current;
    if (!targetStep || isNew || savePending || isDetailFetching) return;

    if (
      targetStep === "fields" &&
      !isActiveTemplate &&
      persistedEditStep !== "CONTRACT_EDIT_BODY"
      // hasRenderedPages 조건은 제거 — BE 가 본문 저장 시 렌더를 스킵할 수 있어
      // 이 조건이 계속 false 라 자동 전진이 막힌다. Step 2 UI 에서 렌더 상태를 안내한다.
    ) {
      pendingStepAfterSaveRef.current = null;
      setStep("fields");
      return;
    }

    if (
      targetStep === "preview" &&
      persistedEditStep === "CONTRACT_EDIT_REVIEW"
      // hasRenderedPreview 조건 제거 — 검토 화면은 편집기 기반이라 렌더 이미지 불필요.
      // (이 조건 때문에 필드 저장 후 검토 자동 전진이 막혔었다)
    ) {
      pendingStepAfterSaveRef.current = null;
      setStep("preview");
    }
  }, [
    isActiveTemplate,
    isDetailFetching,
    isNew,
    persistedEditStep,
    savePending,
  ]);

  const handleStepChange = (nextStep: EditStep) => {
    if (nextStep === "body" && bodyDisabled) {
      if (bodyDisabledReason) alert(bodyDisabledReason);
      return;
    }
    if (nextStep === "fields" && fieldsDisabled) {
      if (fieldsDisabledReason) alert(fieldsDisabledReason);
      return;
    }
    if (nextStep === "preview" && finalPreviewDisabled) {
      if (previewDisabledReason) alert(previewDisabledReason);
      return;
    }
    if (effectiveDirty && nextStep !== step) {
      alert(
        step === "fields"
          ? "필드 저장 후 이동할 수 있어요."
          : "본문 저장 후 이동할 수 있어요.",
      );
      return;
    }
    setStep(nextStep);
  };

  // 임포트 draft를 한 번 시드로 사용했으면 store에서 비움 (다음 진입 시 재사용 안 되게)
  useEffect(() => {
    if (isNew && importedDraft) {
      clearImportedDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    if (savePending) return;
    if (!template.name.trim()) {
      alert("양식 이름을 입력해 주세요");
      return;
    }
    if (isActiveTemplate) {
      alert(activeTemplateLockMessage);
      return;
    }
    // 본문/필드 저장은 아직 완성 전이므로 작성중으로 둔다.
    // 검토 단계의 "작성 완료" 저장만 비활성 완료본으로 전환하고, 실제 활성화는 목록 스위치에서 한다.
    const saveStatus: CdContractStatus =
      step === "preview" ? "CONTRACT_STATUS_INACTIVE" : "CONTRACT_STATUS_DRAFT";
    const saveEditStep: CdContractEditStep = NEXT_EDIT_STEP_AFTER_SAVE[step];
    const targetStepAfterSave: EditStep =
      step === "body" ? "fields" : "preview";
    const isCompletionSave = step === "preview";

    const bodySave = step === "body";
    // 단일레이어: 필드도 본문 docJson inline 노드이므로 body·fields 단계 모두 편집기 JSON 을 직렬화한다.
    //  (fields 단계에서 editorRef 는 숨겨진 body 편집기와 ref 를 공유해 모호하므로 latestEditorJsonRef 우선)
    const editorSave = step === "body" || step === "fields";
    const rawJson = editorSave
      ? (latestEditorJsonRef.current ??
        editorRef.current?.getJSON() ??
        editorSeed?.json ??
        plainToJson(template.body || ""))
      : detail
        ? parseDocJson(detail)
        : (latestEditorJsonRef.current ??
          editorSeed?.json ??
          plainToJson(template.body || ""));
    const strippedDoc = stripLegacyFieldsFromDoc(rawJson);
    const currentJson = strippedDoc.json;
    const bodyText = editorSave
      ? jsonToPlainMarker(currentJson)
      : (detail?.contractContents ?? latestEditorPlainRef.current);
    const docJson = editorSave
      ? JSON.stringify(currentJson)
      : (detail?.contractDocJson ?? JSON.stringify(currentJson));
    // 실제 렌더 이미지가 없으면 (AI 임포트 케이스) docJson 의 페이지 수를 폴백으로 사용.
    // 이렇게 하면 AI 임포트한 다중 페이지 문서에서도 필드 검증이 제대로 동작함.
    const renderedPageCount =
      detail?.renderedPageFileUrls?.length || effectivePageUrls.length;
    const rawFieldsForSave = mergeFields(
      latestFieldsRef.current,
      strippedDoc.fields,
    );
    const bodyLayoutChanged =
      bodySave &&
      (isNew ||
        strippedDoc.fields.length > 0 ||
        savedSnapshotRef.current?.bodyPlain !== bodyText);
    const fieldsForSave = bodyLayoutChanged
      ? []
      : normalizeFieldsForRenderedPages(rawFieldsForSave, renderedPageCount);
    const invalidPlacedFields =
      step !== "body"
        ? fieldsForSave.filter(
            (field) => !isWithinRenderedPageCount(field, renderedPageCount),
          )
        : [];
    if (invalidPlacedFields.length > 0) {
      alert(
        "렌더링된 문서 페이지 밖에 있는 필드가 있어요. 본문을 저장한 뒤 필드를 다시 배치해 주세요.",
      );
      return;
    }
    // 필드는 선택 사항 — 계약서에 서명/체크박스가 필요 없는 경우도 있으므로
    // 필드 0개 저장 허용 (Step 3 검토로 진행 가능).
    latestEditorJsonRef.current = currentJson;
    latestEditorPlainRef.current = bodyText;
    latestFieldsRef.current = fieldsForSave;
    const backendFieldsForSave = toBackendFields(fieldsForSave);
    const savedTemplateSnapshot = {
      ...template,
      body: bodyText,
      fields: fieldsForSave,
    };

    // Dual-write: JSON (편집·재로드) + 마커 평문 (PDF 합성·하위호환). 마커 평문은 백엔드 데이터 흐름용 — 사장님은 안 봄.
    if (isNew) {
      pendingStepAfterSaveRef.current = targetStepAfterSave;
      createMutation.mutate(
        {
          contractName: template.name,
          contractContents: bodyText,
          contractDocJson: docJson,
          cdContractStatus: saveStatus,
          cdEditStep: saveEditStep,
          autoFirstReservYn: template.autoFirstReservYn,
          fields: backendFieldsForSave,
          sourceFileSeq: importedDraftRef.current?.sourceFileSeq,
          autoVarKeys: template.autoVariables?.join(","),
        },
        {
          onSuccess: (newSeq) => {
            // shouldResumeStepRef 를 true 로 두면 detail 재조회 후 useEffect 가
            // resumeStepFromDetail 로 setStep 을 다시 부르면서 방금 우리가 설정한 target step
            // 을 덮어쓸 수 있다. onSuccess 에서 직접 setStep 을 하므로 이 플래그는 초기화.
            shouldResumeStepRef.current = false;
            savedSnapshotRef.current = snapshotOf(
              savedTemplateSnapshot,
              bodyText,
            );
            latestFieldsRef.current = savedTemplateSnapshot.fields;
            setTemplate(savedTemplateSnapshot);
            setIsDirty(false);
            clearImportedDraft();
            // 본문/필드 저장분을 seed 에 즉시 반영 — 필드설정/검토 배경이 리마운트되며 최신 문서로 그려짐
            if (editorSave) {
              seedVersionRef.current += 1;
              setEditorSeed({
                key: `contract-${newSeq}-v${seedVersionRef.current}`,
                json: currentJson,
                text: bodyText,
              });
            }
            // 순차 진행 흐름:
            //   1) 본문 작성(body) 저장 → 필드 설정(fields) 로 자동 이동
            //   2) 필드 설정(fields) 저장 → 검토(preview) 로 자동 이동
            //   3) 검토(preview) 저장(=작성 완료) → 계약서 양식 목록으로 이동
            if (isCompletionSave) {
              history.replace("/business/layout/contract-templates" as any);
            } else {
              // 신규 생성 → URL 을 새 seq 로 replace + 직접 setStep 으로 즉시 전환
              history.replace(
                `/business/layout/contract-template-edit?seq=${newSeq}` as any,
              );
              setStep(targetStepAfterSave);
            }
          },
          onError: (e) => {
            pendingStepAfterSaveRef.current = null;
            console.error(e);
            alert("저장 실패");
          },
        },
      );
    } else if (seqNum != null) {
      pendingStepAfterSaveRef.current = targetStepAfterSave;
      updateMutation.mutate(
        {
          contractSeq: seqNum,
          req: {
            contractName: template.name,
            contractContents: bodyText,
            contractDocJson: docJson,
            cdContractStatus: saveStatus,
            cdEditStep: saveEditStep,
            autoFirstReservYn: template.autoFirstReservYn,
            fields: backendFieldsForSave,
            autoVarKeys: template.autoVariables?.join(","),
          },
        },
        {
          onSuccess: () => {
            // shouldResumeStepRef 를 true 로 두면 detail 재조회 후 useEffect 가
            // resumeStepFromDetail 로 setStep 을 다시 부르면서 방금 우리가 설정한 target step
            // 을 덮어쓸 수 있다. onSuccess 에서 직접 setStep 을 하므로 이 플래그는 초기화.
            shouldResumeStepRef.current = false;
            savedSnapshotRef.current = snapshotOf(
              savedTemplateSnapshot,
              bodyText,
            );
            latestFieldsRef.current = savedTemplateSnapshot.fields;
            setTemplate(savedTemplateSnapshot);
            setIsDirty(false);
            // 본문/필드 저장분을 seed 에 즉시 반영 — 필드설정/검토 배경이 리마운트되며 최신 문서로 그려짐
            if (editorSave) {
              seedVersionRef.current += 1;
              setEditorSeed({
                key: `contract-${seqNum}-v${seedVersionRef.current}`,
                json: currentJson,
                text: bodyText,
              });
            }
            // 완료 저장이면 목록으로, 그 외에는 다음 단계로 자동 이동.
            if (isCompletionSave) {
              history.replace("/business/layout/contract-templates" as any);
            } else {
              setStep(targetStepAfterSave);
            }
          },
          onError: (e) => {
            pendingStepAfterSaveRef.current = null;
            console.error(e);
            alert("저장 실패");
          },
        },
      );
    }
  };

  const handleBack = () => {
    if (
      effectiveDirty &&
      !confirm("저장하지 않은 변경사항이 있어요. 나가시겠어요?")
    ) {
      return;
    }
    if (window.history.length > 1) history.back();
    else history.replace("/business/layout/contract-templates");
  };

  return (
    <>
      <LayoutHeader
        title={isNew ? "새 계약서 템플릿" : "계약서 편집"}
        hasBackBtn
        hasFootPrint={false}
      />
      <ContractPinGate ownerOnly>
        {/* 페이지 = 한 화면 — 외부 스크롤 없음. 헤더·툴바·체크박스 row 는 고정, 에디터 영역만 내부 스크롤.
          레이아웃 래퍼가 이제 자라는 방식이라 편집기는 자체적으로 화면 높이를 고정한다
          (모바일: dvh - 헤더 3.5rem / 데스크톱 카드: vh - py 3rem - 헤더 3.5rem). nav 는 숨김 */}
        <div className="h-[calc(100dvh-3.5rem)] md:h-[calc(100vh-6.5rem)] flex flex-col bg-[#F5F5F5] overflow-hidden">
          <div className="flex-1 min-h-0 flex flex-col bg-white">
            <ContractEditorToolbar
              name={template.name}
              onNameChange={(name) =>
                updateTemplateState((prev) => ({ ...prev, name }))
              }
              step={step}
              onStepChange={handleStepChange}
              bodyDisabled={bodyDisabled}
              bodyDisabledReason={bodyDisabledReason}
              fieldsDisabled={fieldsDisabled}
              fieldsDisabledReason={fieldsDisabledReason}
              previewDisabled={finalPreviewDisabled}
              previewDisabledReason={previewDisabledReason}
              onSave={handleSave}
              onCancel={handleBack}
              isDirty={effectiveDirty}
              isSaving={savePending}
              saveLabel={
                step === "body"
                  ? "본문 저장"
                  : step === "fields"
                    ? "필드 저장"
                    : "작성 완료"
              }
              readOnly={isActiveTemplate}
              readOnlyReason={activeTemplateLockMessage}
            />

            {/* 기본 설정 — 사업장당 1개만 (체크하면 다른 양식의 기본 설정이 자동 해제됨) */}
            <div className="px-3 py-2 bg-white border-b border-[#F0F0F0]">
              <label
                className="inline-flex items-center gap-1.5 cursor-pointer"
                title="사업장당 1개만 지정 가능 — 체크하면 기존 기본 양식은 자동 해제됩니다."
              >
                <input
                  type="checkbox"
                  checked={template.autoFirstReservYn === "Y"}
                  disabled={isActiveTemplate}
                  onChange={(e) =>
                    updateTemplateState((prev) => ({
                      ...prev,
                      autoFirstReservYn: e.target.checked ? "Y" : "N",
                    }))
                  }
                />
                <span className="text-[12px] text-[#111]">
                  기본 설정{" "}
                  <span className="text-[#999]">(첫 이용 시 자동 발송)</span>
                </span>
              </label>
            </div>

            {/* 본문(에디터) 영역만 flex-1 + min-h-0 으로 남은 공간 차지, overflow 는 PanZoomCanvas 가 자체 처리 */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {/* Step 3 검토 — Step 2 와 완전 동일한 편집기(readOnly)+필드 오버레이 시각화.
                필드 박스는 대시 스타일로 보여 사업자가 최종 배치 확인.
                (실 서명·도장·체크는 보호자 서명 화면과 최종 PDF 에서 렌더러가 처리) */}
              {step === "preview" && editorSeed && (
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="px-3 py-2 bg-white border-b border-[#F0F0F0] text-[11px] text-[#666] flex items-center gap-2">
                    <span>📋</span>
                    <span>
                      검토 화면 — 필드 배치를 최종 확인하세요. 서명·도장은
                      보호자 서명 시 이 자리에 채워집니다.
                    </span>
                  </div>
                  <div className="flex flex-1 min-h-0 p-3">
                    {/* 단일레이어: 필드는 docJson inline 노드로 편집기에 그대로 렌더됨 (오버레이 불필요) */}
                    <ContractRichEditor
                      key={`preview-${editorSeed.key}`}
                      initialJson={editorSeed.json}
                      initialText={editorSeed.text}
                      readOnly
                      ref={editorRef}
                    />
                  </div>
                </div>
              )}
              {/* Step 2 — 단일레이어 필드 설정. 본문 편집기와 동일하되 fieldMode:
                  · 텍스트/변수는 잠금(못 건드림) → "필드일 때는 필드만"
                  · 툴바 버튼이 inline contractField 노드를 커서 위치에 삽입
                  · 필드 클릭 시 필수/선택·도장선택·삭제 팝오버 (오버레이 폐기) */}
              {step === "fields" && editorSeed && (
                <div className="flex-1 min-h-0 flex flex-col">
                  <ContractFieldToolbar
                    onAdd={(type) => {
                      if (isActiveTemplate) return;
                      editorRef.current?.insertField(type);
                    }}
                    disabled={isActiveTemplate}
                  />
                  <div className="flex flex-1 min-h-0 p-3">
                    <ContractRichEditor
                      key={`fields-${editorSeed.key}`}
                      initialJson={editorSeed.json}
                      initialText={editorSeed.text}
                      readOnly={isActiveTemplate}
                      fieldMode={!isActiveTemplate}
                      ref={editorRef}
                      onChange={(json, plain) => {
                        if (isActiveTemplate) return;
                        latestEditorJsonRef.current = json;
                        latestEditorPlainRef.current = plain;
                        setIsDirty(isSnapshotDirty(template, plain));
                      }}
                    />
                  </div>
                </div>
              )}
              <div
                className={`${step === "body" ? "flex" : "hidden"} flex-1 min-h-0 p-3`}
              >
                {/* p-3 wrapper 도 flex-1 min-h-0 으로 — 안의 ContractRichEditor 가 h-full 체인 받을 수 있도록 */}
                {editorSeed ? (
                  <ContractRichEditor
                    key={editorSeed.key}
                    ref={editorRef}
                    initialJson={editorSeed.json}
                    initialText={editorSeed.text}
                    readOnly={isActiveTemplate}
                    onChange={(json, plain) => {
                      if (isActiveTemplate) return;
                      const ignoreDirty = ignoreNextEditorChangeRef.current;
                      if (ignoreDirty) {
                        ignoreNextEditorChangeRef.current = false;
                      }
                      latestEditorJsonRef.current = json;
                      latestEditorPlainRef.current = plain;
                      if (ignoreDirty) {
                        savedSnapshotRef.current = snapshotOf(template, plain);
                        setIsDirty(false);
                        return;
                      }
                      setIsDirty(isSnapshotDirty(template, plain));
                    }}
                  />
                ) : (
                  <div className="flex-1 min-h-0 flex items-center justify-center text-[13px] text-[#999] bg-white rounded-md border border-[#E9E9E9]">
                    계약서 본문을 불러오는 중입니다.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ContractPinGate>
    </>
  );
}
